import { useEffect, useMemo } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import type { Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import Color from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import Highlight from '@tiptap/extension-highlight';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableHeader } from '@tiptap/extension-table-header';
import { TableCell } from '@tiptap/extension-table-cell';
import HorizontalRule from '@tiptap/extension-horizontal-rule';
import { cn } from '@/lib/utils';
import { EmailToolbar, type TemplateContext } from './EmailToolbar';
import './EmailEditor.css';

export type { TemplateContext };

export interface EmailEditorProps {
  /** Conteúdo HTML inicial */
  initialContent?: string;
  /** Callback quando conteúdo muda — retorna HTML */
  onChange?: (html: string) => void;
  /** Placeholder quando vazio */
  placeholder?: string;
  /** Altura mínima do editor */
  minHeight?: number;
  /** Altura máxima (scroll interno) */
  maxHeight?: number;
  /** Modo compacto para reply inline */
  compact?: boolean;
  /** Desabilita edição */
  readOnly?: boolean;
  /** Ref para acessar o editor programaticamente */
  editorRef?: React.MutableRefObject<Editor | null>;
  /** Assinatura HTML para inserir automaticamente */
  signature?: string;
  /** Mensagem original citada (para reply/forward) */
  quotedMessage?: string;
  /** Callback para anexar arquivo */
  onAttach?: () => void;
  /** @deprecated Use templateContext + onTemplateSelect instead */
  onTemplates?: () => void;
  /** Callback para IA */
  onAI?: () => void;
  /** Whether AI mode is currently active — highlights the IA button in toolbar */
  aiModeActive?: boolean;
  /** Deal/lead/user context for merge field auto-resolution */
  templateContext?: TemplateContext;
  /** Called when a template is applied — use to fill the subject field at parent level */
  onTemplateSelect?: (bodyHtml: string, subject?: string | null) => void;
  /** Contagem de anexos */
  attachmentCount?: number;
  /** Classes CSS adicionais */
  className?: string;
  /** ID do elemento */
  id?: string;
}

/** Detecta tags HTML no meio do texto (ex.: templates com <span class="template-filled">) */
function stringContainsHtmlTags(s: string): boolean {
  return /<\/?[a-z][\w-]*\b/i.test(s);
}

/** Blocos dentro de <p> são HTML inválido e o TipTap pode acabar com documento vazio */
function hasBlockLevelHtml(s: string): boolean {
  return /<\s*(p|div|h[1-6]|ul|ol|table|blockquote|article|section)\b/i.test(s);
}

/**
 * Texto simples ou HTML parcial → fragmento que o TipTap renderiza de forma confiável.
 * Conteúdo que começa com texto mas inclui tags (marcação de template) não pode ser escapado como texto puro.
 * Spans de template sem Mark dedicado: o TipTap preserva o texto interno ao ignorar o span desconhecido.
 */
function coerceBodyHtmlForTipTap(raw: string | undefined): string {
  if (raw === undefined || raw === null) return '<p></p>';
  const s = String(raw);
  if (!s.trim()) return '<p></p>';

  if (stringContainsHtmlTags(s)) {
    const t = s.trim();
    if (/^<(p|div|h[1-6]|ul|ol|blockquote|table|article|section)\b/i.test(t)) {
      return s;
    }
    if (hasBlockLevelHtml(s)) {
      return s;
    }
    return `<p>${s}</p>`;
  }

  const escape = (t: string) =>
    t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const withBr = escape(s).split(/\n/).join('<br />');
  return `<p>${withBr}</p>`;
}

function buildInitialContent(
  initialContent?: string,
  signature?: string,
  quotedMessage?: string,
): string {
  if (initialContent !== undefined && initialContent !== null && String(initialContent).trim() !== '') {
    return coerceBodyHtmlForTipTap(initialContent);
  }

  let html = '<p></p>';

  if (signature) {
    html += `<br/><br/><div class="email-signature" style="border-top: 1px solid hsl(var(--border)); padding-top: 12px; margin-top: 24px; color: hsl(var(--muted-foreground)); font-size: 13px;">${signature}</div>`;
  }

  if (quotedMessage) {
    html += `<div class="email-quoted" style="border-left: 3px solid hsl(var(--border)); padding-left: 12px; margin-top: 16px; color: hsl(var(--muted-foreground));"><p><strong>Mensagem original</strong></p>${quotedMessage}</div>`;
  }

  return html;
}

export function EmailEditor({
  initialContent,
  onChange,
  placeholder = 'Escreva sua mensagem...',
  minHeight = 200,
  maxHeight = 400,
  compact = false,
  readOnly = false,
  editorRef,
  signature,
  quotedMessage,
  onAttach,
  onTemplates,
  onAI,
  aiModeActive,
  attachmentCount = 0,
  templateContext,
  onTemplateSelect,
  className,
  id,
}: EmailEditorProps) {
  const content = useMemo(
    () => buildInitialContent(initialContent, signature, quotedMessage),
    // Only compute once on mount — external sync handles updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const extensions = useMemo(() => [
    StarterKit.configure({
      heading: { levels: [1, 2, 3] },
      horizontalRule: false, // use dedicated extension
    }),
    Underline,
    Link.configure({
      openOnClick: false,
      HTMLAttributes: {
        class: 'text-primary underline cursor-pointer',
      },
    }),
    Image.configure({
      inline: true,
      allowBase64: true,
    }),
    TextAlign.configure({
      types: ['heading', 'paragraph'],
    }),
    Placeholder.configure({
      placeholder,
    }),
    Color,
    TextStyle,
    Highlight.configure({ multicolor: true }),
    Table.configure({ resizable: true }),
    TableRow,
    TableHeader,
    TableCell,
    HorizontalRule,
  ], [placeholder]);

  const editor = useEditor({
    extensions,
    content,
    editable: !readOnly,
    editorProps: {
      attributes: {
        id: id || '',
        class: 'prose prose-sm max-w-none focus:outline-none px-3.5 py-3 text-sm text-foreground',
        'data-placeholder': placeholder,
      },
    },
    onUpdate: ({ editor: ed }) => {
      onChange?.(ed.getHTML());
    },
  });

  // Expose editor via ref
  useEffect(() => {
    if (editorRef && editor) {
      editorRef.current = editor;
    }
  }, [editor, editorRef]);

  // Sync external content changes (reply/forward/template injection / description inicial)
  // Only update if editor is not focused to avoid cursor jumps
  useEffect(() => {
    if (!editor || initialContent === undefined) return;
    const nextHtml = coerceBodyHtmlForTipTap(initialContent);
    if (nextHtml !== editor.getHTML() && !editor.isFocused) {
      editor.commands.setContent(nextHtml, { emitUpdate: false });
    }
  }, [initialContent, editor]);

  // Update editable state
  useEffect(() => {
    if (editor) {
      editor.setEditable(!readOnly);
    }
  }, [readOnly, editor]);

  if (!editor) return null;

  return (
    <div
      className={cn(
        'tiptap-editor rounded-lg border border-border bg-white transition-all duration-150',
        'focus-within:border-primary focus-within:ring-1 focus-within:ring-primary/20',
        compact && 'tiptap-editor--compact',
        className,
      )}
      style={{
        '--editor-min-height': `${minHeight}px`,
        '--editor-max-height': `${maxHeight}px`,
      } as React.CSSProperties}
    >
      {!readOnly && (
        <EmailToolbar
          editor={editor}
          onAttach={onAttach}
          onTemplates={onTemplates}
          onAI={onAI}
          aiModeActive={aiModeActive}
          attachmentCount={attachmentCount}
          compact={compact}
          templateContext={templateContext}
          onTemplateSelect={onTemplateSelect}
        />
      )}
      <EditorContent editor={editor} />
    </div>
  );
}
